import { RevenueMarketSelect, parseRevenueMarket, type RevenueMarket } from '@/components/travel/revenue-market';
import { DirectionSelect, parseDirection, type Direction } from '@/components/travel/direction';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type InvoiceType = 'PROFORMA' | 'INVOICE' | 'CREDIT_NOTE';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

interface InvoiceFormData {
  customerId: string;
  bookingId?: string;
  invoiceType: InvoiceType;
  dueDate: string;
  currency: string;
  notes?: string;
  terms?: string;
}

interface InvoiceModalProps {
  defaultDirection?: Direction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (invoiceId: string) => void;
}

function getDueDateString(days: number = 14): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0] ?? '';
}

export function InvoiceModal({
  open,
  onOpenChange,
  onSuccess,
  defaultDirection,
}: InvoiceModalProps) {
  const { t } = useTranslation('invoices');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [revenueMarket, setRevenueMarket] = useState<RevenueMarket | undefined>();
  const [direction, setDirection] = useState<Direction | undefined>(defaultDirection);
  useEffect(() => { if (open) { setDirection(defaultDirection); setRevenueMarket(undefined); } }, [open, defaultDirection]);
  const [items, setItems] = useState<InvoiceItem[]>([
    { description: '', quantity: 1, unitPrice: 0, taxRate: 0 },
  ]);
  const user = useAuthStore((state) => state.user);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<InvoiceFormData>({
    defaultValues: {
      invoiceType: 'INVOICE',
      dueDate: getDueDateString(),
      currency: 'USD',
    },
  });

  const { data: customers, isLoading: customersLoading } = trpc.customer.list.useQuery({
    page: 1,
  });

  const { data: bookings, isLoading: bookingsLoading } = trpc.booking.list.useQuery({
    direction: defaultDirection,
    page: 1,
  });

  const watchedBookingId = watch('bookingId');

  // Fetch booking details when a booking is selected
  const { data: selectedBooking } = trpc.booking.byId.useQuery(
    watchedBookingId ?? '',
    { enabled: !!watchedBookingId && watchedBookingId !== '_none' }
  );

  // Auto-populate invoice when booking is selected
  useEffect(() => {
    if (selectedBooking) {
      // Set customer from booking
      setValue('customerId', selectedBooking.customer.id);
      setValue('currency', selectedBooking.currency);

      // Create line items from booking
      const newItems: InvoiceItem[] = [];

      if (selectedBooking.totalAdults > 0) {
        const adultPrice = selectedBooking.totalAmount /
          (selectedBooking.totalAdults +
           (selectedBooking.totalChildren * 0.75) +
           (selectedBooking.totalInfants * 0.1));

        newItems.push({
          description: `${selectedBooking.package?.name || 'Travel Package'} - Adults`,
          quantity: selectedBooking.totalAdults,
          unitPrice: Math.round(adultPrice * 100) / 100,
          taxRate: 0,
        });

        if (selectedBooking.totalChildren > 0) {
          newItems.push({
            description: `${selectedBooking.package?.name || 'Travel Package'} - Children`,
            quantity: selectedBooking.totalChildren,
            unitPrice: Math.round(adultPrice * 0.75 * 100) / 100,
            taxRate: 0,
          });
        }

        if (selectedBooking.totalInfants > 0) {
          newItems.push({
            description: `${selectedBooking.package?.name || 'Travel Package'} - Infants`,
            quantity: selectedBooking.totalInfants,
            unitPrice: Math.round(adultPrice * 0.1 * 100) / 100,
            taxRate: 0,
          });
        }
      } else {
        // Fallback: single line item with total
        newItems.push({
          description: selectedBooking.package?.name || 'Travel Package',
          quantity: 1,
          unitPrice: selectedBooking.totalAmount,
          taxRate: 0,
        });
      }

      setItems(newItems);
    }
  }, [selectedBooking, setValue]);

  const createMutation = trpc.invoice.create.useMutation({
    onSuccess: (data) => {
      reset();
      setItems([{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]);
      onOpenChange(false);
      onSuccess?.(data.id);
      showSuccessToast(t('modal.success'));
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('modal.error'));
    },
  });

  const watchedCustomerId = watch('customerId');
  const watchedCurrency = watch('currency');

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const newItems = [...items];
    const item = newItems[index];
    if (item) {
      (item[field] as string | number) = value;
      setItems(newItems);
    }
  };

  const calculateItemTotal = (item: InvoiceItem) => {
    const subtotal = item.quantity * item.unitPrice;
    const tax = (subtotal * item.taxRate) / 100;
    return subtotal + tax;
  };

  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxTotal = items.reduce((sum, item) => {
    const itemSubtotal = item.quantity * item.unitPrice;
    return sum + (itemSubtotal * item.taxRate) / 100;
  }, 0);
  const grandTotal = subtotal + taxTotal;

  const onSubmit = (data: InvoiceFormData) => {
    if (!user?.id) return;

    // Validate items
    const validItems = items.filter((item) => item.description.trim() && item.unitPrice > 0);
    if (validItems.length === 0) {
      return;
    }

    setIsSubmitting(true);
    createMutation.mutate({
      customerId: data.customerId,
      direction,
      revenueMarket,
      bookingId: data.bookingId || undefined,
      createdById: user.id,
      invoiceType: data.invoiceType,
      dueDate: new Date(data.dueDate),
      currency: data.currency,
      notes: data.notes,
      terms: data.terms,
      items: validItems,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('modal.createInvoice')}</DialogTitle>
          <DialogDescription>
            {t('modal.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <DirectionSelect value={selectedBooking ? selectedBooking.direction : direction} disabled={!!selectedBooking} onChange={value => setDirection(parseDirection(value))} />
          <RevenueMarketSelect value={selectedBooking ? selectedBooking.revenueMarket : revenueMarket} disabled={!!selectedBooking} onChange={value => setRevenueMarket(parseRevenueMarket(value))} />
          {/* Customer and Type */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('modal.customer')} *</Label>
              <Combobox
                options={
                  customersLoading
                    ? []
                    : (customers?.customers.map((customer) => ({
                        value: customer.id,
                        label: `${customer.firstName} ${customer.lastName}`,
                      })) ?? [])
                }
                value={watchedCustomerId || ''}
                onValueChange={(value) => setValue('customerId', value)}
                placeholder={t('modal.selectCustomer')}
                searchPlaceholder={t('modal.searchCustomers')}
              />
              {errors.customerId && (
                <p className="text-sm text-red-500">{errors.customerId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('modal.invoiceType')}</Label>
              <Combobox
                options={[
                  { value: 'INVOICE', label: t('modal.taxInvoice') },
                  { value: 'PROFORMA', label: t('modal.proformaInvoice') },
                  { value: 'CREDIT_NOTE', label: t('modal.creditNote') },
                ]}
                value={watch('invoiceType') || 'INVOICE'}
                onValueChange={(value) => setValue('invoiceType', value as InvoiceType)}
                placeholder={t('modal.selectType')}
                searchPlaceholder="Search..."
              />
            </div>
          </div>

          {/* Booking and Due Date */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>{t('modal.linkToBooking')}</Label>
              <Combobox
                options={[
                  { value: '_none', label: t('modal.none') },
                  ...(bookingsLoading
                    ? []
                    : (bookings?.bookings.map((booking) => ({
                        value: booking.id,
                        label: `${booking.bookingReference} - ${booking.customer.firstName} ${booking.customer.lastName}`,
                      })) ?? [])),
                ]}
                value={watchedBookingId || '_none'}
                onValueChange={(value) => setValue('bookingId', value === '_none' ? undefined : value)}
                placeholder={t('modal.selectBooking')}
                searchPlaceholder={t('modal.searchBookings')}
              />
              {selectedBooking && (
                <p className="text-xs text-muted-foreground">
                  {t('modal.autoPopulated')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">{t('modal.dueDate')} *</Label>
              <Input
                id="dueDate"
                type="date"
                {...register('dueDate', { required: t('modal.dueDateRequired') })}
              />
              {errors.dueDate && (
                <p className="text-sm text-red-500">{errors.dueDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('modal.currency')}</Label>
              <Combobox
                options={[
                  { value: 'USD', label: 'USD - US Dollar' },
                  { value: 'EUR', label: 'EUR - Euro' },
                  { value: 'GBP', label: 'GBP - British Pound' },
                  { value: 'TRY', label: 'TRY - Turkish Lira' },
                ]}
                value={watchedCurrency || 'USD'}
                onValueChange={(value) => setValue('currency', value)}
                placeholder={t('modal.selectCurrency')}
                searchPlaceholder={t('modal.searchCurrencies')}
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t('modal.lineItems')}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="mr-1 h-4 w-4" />
                {t('modal.addItem')}
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="grid gap-3 rounded-lg border p-3 sm:grid-cols-12"
                >
                  <div className="sm:col-span-5">
                    <Label className="text-xs text-muted-foreground">{t('modal.description')}</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder={t('modal.itemDescription')}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">{t('modal.qty')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">{t('modal.unitPrice')}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', Number(e.target.value))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">{t('modal.taxPercent')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={item.taxRate}
                      onChange={(e) => updateItem(index, 'taxRate', Number(e.target.value))}
                    />
                  </div>
                  <div className="flex items-end sm:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="text-right text-sm font-medium sm:col-span-12">
                    {t('modal.itemTotal')}: {formatCurrency(calculateItemTotal(item), watchedCurrency)}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2 rounded-lg bg-muted p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('modal.subtotal')}</span>
                  <span>{formatCurrency(subtotal, watchedCurrency)}</span>
                </div>
                {taxTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('modal.tax')}</span>
                    <span>{formatCurrency(taxTotal, watchedCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 font-bold">
                  <span>{t('modal.total')}</span>
                  <span>{formatCurrency(grandTotal, watchedCurrency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="notes">{t('modal.notes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('modal.notesPlaceholder')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="terms">{t('modal.paymentTerms')}</Label>
              <Textarea
                id="terms"
                {...register('terms')}
                placeholder={t('modal.termsPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          {/* Error Messages */}
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('modal.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !watchedCustomerId}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('modal.createInvoice')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
